import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Row, Col, Container, Modal, Form, Button, Dropdown, Badge } from 'react-bootstrap'
import Card from '../../../components/Card'
import { InView } from "react-intersection-observer";
import { Link, useNavigate, Route } from 'react-router-dom'
import { apiHandler } from '../../../api/apihandler';
import { LEAVE_APPLICATION_API, LEAVE_APPLICATION_STORE_API, LEAVE_APPLICATION_DELETE_API } from "../../../api/constants";
import LoaderModal from '../../../components/loaderModal';
import Swal from 'sweetalert2'
import Moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import { handleImageUpload } from '../../../api/uploads';

// image
import loader from '../../../assets/images/page-img/page-load-loader.gif'
import NoDataFound from '../../../components/noDataFound';

const LeaveApplication = () => {

   const navigate = useNavigate();
   const [leaveApplicationList, setLeaveApplicationList] = useState([]);
   const [page, setPage] = useState(1);
   const [maxPage, setMaxPage] = useState(1);
   const [showLoader, setShowLoader] = useState(false);
   const [showMoreDataLoader, setShowMoreDataLoader] = useState(false);
   const [showApplyModal, setShowApplyModal] = useState(false);

   // Form state
   const [leaveType, setLeaveType] = useState('');
   const [fromDate, setFromDate] = useState('');
   const [toDate, setToDate] = useState('');
   const [reason, setReason] = useState('');
   const [parentSignature, setParentSignature] = useState('');
   const [attachment, setAttachment] = useState(null);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [expandedReasons, setExpandedReasons] = useState({});
   const [currentCalendarDate, setCurrentCalendarDate] = useState(Moment());

   const signaturePadRef = useRef(null);

   const [isFirst, setIsFirst] = useState(true);
   useEffect(() => {
      window.scrollTo({
         top: 0,
         behavior: "smooth",
      });
      setIsFirst(false)
   }, [isFirst]);

   useEffect(() => {
      if (localStorage.getItem("uzoneAuthToken")) {
         setLeaveApplicationList([])
         getLeaveApplicationList(1)
      } else {
         navigate('/')
      }
   }, []);

   const loadMore = (inView) => {
      if (inView) {
         if (page < maxPage) {
            getLeaveApplicationList(page + 1)
            setPage(page + 1)
         }
      }
   };

   const handleCloseApplyModal = () => {
      setShowApplyModal(false);
      resetForm();
   };

   const handleShowApplyModal = () => {
      setShowApplyModal(true);
   };

   const resetForm = () => {
      setLeaveType('');
      setFromDate('');
      setToDate('');
      setReason('');
      setParentSignature('');
      setAttachment(null);
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   const handleClearSignature = () => {
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   async function getLeaveApplicationList(pageVal) {
      if (pageVal === 1) {
         setShowLoader(true)
      } else {
         setShowMoreDataLoader(true)
      }
      try {
         const res = await apiHandler(LEAVE_APPLICATION_API, "POST", {
            page: pageVal,
            limit: 10
         })
         if (res.data?.response?.status === 200) {
            setMaxPage(res?.data?.data?.data?.meta?.last_page)
            if (res?.data?.data?.data?.meta?.total > 0) {
               if (pageVal === 1) {
                  setLeaveApplicationList(res?.data?.data?.data?.data)
               } else {
                  setLeaveApplicationList(leaveApplicationList.concat(res?.data?.data?.data?.data))
               }
            }
         } else {
            // Swal.fire({
            //    title: 'Error',
            //    text: res?.response?.data?.data?.message || 'Failed to load leave applications',
            //    icon: 'error',
            //    confirmButtonText: 'Okay'
            // })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setShowMoreDataLoader(false)
         setShowLoader(false)
      }
   }

   async function handleApplyLeave(e) {
      e.preventDefault();

      // Check if signature canvas has content
      const hasNewSignature = signaturePadRef.current && !signaturePadRef.current.isEmpty();

      // Validation - Leave Type
      if (!leaveType || leaveType.trim() === '') {
         Swal.fire({
            title: 'Warning',
            text: 'Please select a leave type',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      // Validation - Dates
      if (!fromDate || !toDate) {
         Swal.fire({
            title: 'Warning',
            text: 'Please select both from date and to date',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      // Validate date format
      if (!Moment(fromDate, 'YYYY-MM-DD', true).isValid() || !Moment(toDate, 'YYYY-MM-DD', true).isValid()) {
         Swal.fire({
            title: 'Warning',
            text: 'Invalid date format. Please select valid dates',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      // Validate date range
      if (new Date(toDate) < new Date(fromDate)) {
         Swal.fire({
            title: 'Warning',
            text: 'To date cannot be before from date',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      // Validation - Reason
      if (!reason || reason.trim() === '') {
         Swal.fire({
            title: 'Warning',
            text: 'Please provide a reason for leave',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      if (reason.trim().length < 10) {
         Swal.fire({
            title: 'Warning',
            text: 'Reason must be at least 10 characters long',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      // Validation - Signature
      if (!hasNewSignature && !parentSignature) {
         Swal.fire({
            title: 'Warning',
            text: 'Please provide parent signature',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      setIsSubmitting(true)
      try {
         let signatureUrl = parentSignature;

         // Upload signature to AWS if there's a new signature
         if (hasNewSignature) {
            const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
            const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
            const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
            signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
         }

         let attachmentUrl = '';
         // Upload attachment to AWS if there's a file
         if (attachment) {
            attachmentUrl = await handleImageUpload(attachment, 'leave-attachments/', attachment.type);
         }

         // Keep dates in yyyy-MM-dd format for API (date input already provides this format)
         const formData = new FormData();
         formData.append('leave_type', leaveType.trim());
         formData.append('from_date', fromDate);
         formData.append('to_date', toDate);
         formData.append('reason', reason.trim());
         formData.append('parent_signature', signatureUrl);
         if (attachmentUrl) {
            formData.append('attachment', attachmentUrl);
         }

         const res = await apiHandler(LEAVE_APPLICATION_STORE_API, "POST", formData)
         if (res.data?.response?.status === 200 || res.data?.response?.status === 201) {
            Swal.fire({
               title: 'Success',
               text: res?.data?.data?.message || 'Leave application submitted successfully',
               icon: 'success',
               confirmButtonText: 'Okay'
            })
            handleCloseApplyModal();
            setLeaveApplicationList([])
            getLeaveApplicationList(1)
         } else {
            Swal.fire({
               title: 'Error',
               text: res?.response?.data?.data?.message || 'Failed to submit leave application',
               icon: 'error',
               confirmButtonText: 'Okay'
            })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setIsSubmitting(false)
      }
   }

   async function deleteLeaveApplication(leaveId) {
      Swal.fire({
         title: 'Warning!',
         text: 'Are you sure you want to delete this leave application?',
         icon: 'warning',
         showCancelButton: true,
         showConfirmButton: true,
         cancelButtonText: 'Cancel',
         confirmButtonText: 'Delete',
         confirmButtonColor: '#d33'
      }).then(async (result) => {
         if (result.isConfirmed) {
            try {
               const res = await apiHandler(LEAVE_APPLICATION_DELETE_API, "POST", {
                  id: leaveId
               })
               if (res.data?.response?.status === 200) {
                  Swal.fire({
                     title: 'Success',
                     text: res?.data?.data?.message || 'Leave application deleted successfully',
                     icon: 'success',
                     confirmButtonText: 'Okay'
                  })
                  setLeaveApplicationList([])
                  getLeaveApplicationList(1)
               } else {
                  Swal.fire({
                     title: 'Error',
                     text: res?.response?.data?.data?.message || 'Failed to delete leave application',
                     icon: 'error',
                     confirmButtonText: 'Okay'
                  })
               }
            } catch (error) {
               console.error(error);
               Swal.fire({
                  title: 'Error',
                  text: 'Something went wrong. Please try again.',
                  icon: 'error',
                  confirmButtonText: 'Okay'
               })
            }
         }
      })
   }

   const getStatusBadge = (status) => {
      switch (status) {
         case 1:
            return <Badge bg="primary">Approved</Badge>;
         case 2:
            return <Badge bg="danger">Rejected</Badge>;
         case 0:
            return <Badge bg="warning" text="dark">Pending</Badge>;
         default:
            return <Badge bg="secondary">Unknown</Badge>;
      }
   };

   const toggleReadMore = (id) => {
      setExpandedReasons(prev => ({
         ...prev,
         [id]: !prev[id]
      }));
   };

   // Calendar helper functions
   const getLeavesForDate = (date) => {
      return leaveApplicationList.filter(leave => {
         const fromDate = Moment(leave.from_date);
         const toDate = Moment(leave.to_date);
         return date.isBetween(fromDate, toDate, 'day', '[]');
      });
   };

   const generateCalendarDays = () => {
      const startOfMonth = Moment(currentCalendarDate).startOf('month');
      const endOfMonth = Moment(currentCalendarDate).endOf('month');
      const startDate = Moment(startOfMonth).startOf('week');
      const endDate = Moment(endOfMonth).endOf('week');

      const days = [];
      let day = Moment(startDate);

      while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
         days.push(Moment(day));
         day = day.add(1, 'day');
      }

      return days;
   };

   const goToPreviousMonth = () => {
      setCurrentCalendarDate(Moment(currentCalendarDate).subtract(1, 'month'));
   };

   const goToNextMonth = () => {
      setCurrentCalendarDate(Moment(currentCalendarDate).add(1, 'month'));
   };

   const goToToday = () => {
      setCurrentCalendarDate(Moment());
   };


   return (
      <>
         <LoaderModal showLoader={showLoader} />
         <div id='content-page' className='content-page scroller'>
            <Container>
               <Row>
                     {/* Leave Applications List — order-2 below lg so the Apply/Stats/Calendar
                         column (which used to just be "whatever came second" once stacked on
                         mobile) shows above this list instead of under it; order-lg-1 restores
                         the original left-side desktop position once the two columns sit
                         side by side again. */}
                     <Col lg={8} className="mt-4 mt-lg-0 order-2 order-lg-1">
                        <Card>
                           <Card.Header className="d-flex justify-content-between">
                              <div className="header-title">
                                 <h4 className="card-title">My Leave Applications</h4>
                              </div>
                           </Card.Header>
                           <Card.Body className="p-0">
                              <div className="p-3">
                                 {leaveApplicationList?.length > 0 ? leaveApplicationList.map((elm, i) => (
                                    <div key={elm?.id} className="border rounded p-3 mb-3 bg-white shadow-sm" style={{ transition: 'all 0.3s ease' }}>
                                       {/* Header Section */}
                                       <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                          <div className="d-flex align-items-center gap-2">
                                             <div className={`d-flex align-items-center justify-content-center rounded-circle ${elm?.status === 1 ? 'bg-soft-primary' : elm?.status === 2 ? 'bg-soft-danger' : 'bg-soft-warning'}`}
                                                  style={{ 
                                                     width: '45px', 
                                                     height: '45px'
                                                  }}>
                                                <i className={`ri-${elm?.leave_type === 'sick' ? 'heart-pulse' : elm?.leave_type === 'vacation' ? 'flight-takeoff' : elm?.leave_type === 'emergency' ? 'alarm-warning' : elm?.leave_type === 'family' ? 'parent' : 'calendar'}-line ${elm?.status === 1 ? 'text-primary' : elm?.status === 2 ? 'text-danger' : 'text-warning'}`}
                                                   style={{ 
                                                      fontSize: '24px'
                                                   }}></i>
                                             </div>
                                             <div>
                                                <h5 className="mb-0 text-capitalize fw-bold text-primary">
                                                   {elm?.leave_type} Leave
                                                </h5>
                                                {elm?.student && (
                                                   <small className="text-muted">
                                                      <i className="ri-user-line me-1"></i>
                                                      {elm?.student?.first_name} {elm?.student?.last_name}
                                                   </small>
                                                )}
                                             </div>
                                          </div>
                                          <div className="d-flex align-items-center gap-2">
                                             {getStatusBadge(elm?.status)}
                                             {elm?.status === 0 && (
                                                <Button 
                                                   variant="outline-danger" 
                                                   size="sm" 
                                                   onClick={() => deleteLeaveApplication(elm?.id)}
                                                   className="d-flex align-items-center gap-1">
                                                   <i className="ri-delete-bin-line"></i>
                                                   <span className="d-none d-md-inline">Delete</span>
                                                </Button>
                                             )}
                                          </div>
                                       </div>

                                       {/* Duration Section */}
                                       <div className="mb-3">
                                          <div className="d-flex align-items-center gap-2 mb-2">
                                             <i className="ri-calendar-2-line text-primary" style={{ fontSize: '20px' }}></i>
                                             <h6 className="mb-0 fw-bold">Duration</h6>
                                          </div>
                                          <div className="ms-4 d-flex flex-wrap align-items-center gap-2">
                                             <span className="badge bg-light text-dark px-3 py-2">
                                                <i className="ri-calendar-check-line me-1"></i>
                                                {Moment(elm?.from_date).format('MMM D, YYYY')}
                                             </span>
                                             <i className="ri-arrow-right-line text-muted"></i>
                                             <span className="badge bg-light text-dark px-3 py-2">
                                                <i className="ri-calendar-check-line me-1"></i>
                                                {Moment(elm?.to_date).format('MMM D, YYYY')}
                                             </span>
                                             <span className="badge bg-primary text-white">
                                                {elm?.total_days} {elm?.total_days === 1 ? 'Day' : 'Days'}
                                             </span>
                                          </div>
                                       </div>

                                       {/* Reason Section */}
                                       <div className="mb-3">
                                          <div className="d-flex align-items-center gap-2 mb-2">
                                             <i className="ri-file-text-line text-primary" style={{ fontSize: '20px' }}></i>
                                             <h6 className="mb-0 fw-bold">Reason</h6>
                                          </div>
                                          <div className="ms-4">
                                             <p className="mb-1 text-muted" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                                {elm?.reason?.length > 150 && !expandedReasons[elm?.id]
                                                   ? `${elm?.reason.substring(0, 150)}...`
                                                   : elm?.reason}
                                             </p>
                                             {elm?.reason?.length > 150 && (
                                                <Button
                                                   variant="link"
                                                   size="sm"
                                                   className="p-0 text-decoration-none text-primary"
                                                   style={{ fontSize: '14px' }}
                                                   onClick={() => toggleReadMore(elm?.id)}>
                                                   {expandedReasons[elm?.id] ? (
                                                      <>
                                                         <i className="ri-arrow-up-s-line"></i> Show Less
                                                      </>
                                                   ) : (
                                                      <>
                                                         <i className="ri-arrow-down-s-line"></i> Read More
                                                      </>
                                                   )}
                                                </Button>
                                             )}
                                          </div>
                                       </div>

                                       {/* Attachments Section */}
                                       <Row className="g-3">
                                          {elm?.parent_signature && (
                                             <Col md={6}>
                                                <div className="border rounded p-2 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                                   <div className="d-flex align-items-center gap-2 mb-2">
                                                      <i className="ri-pen-nib-line text-primary" style={{ fontSize: '18px' }}></i>
                                                      <small className="fw-bold">Parent Signature</small>
                                                   </div>
                                                   <div className="border rounded p-2 bg-white" style={{ height: '100px' }}>
                                                      <img 
                                                         src={elm?.parent_signature} 
                                                         alt="Parent Signature" 
                                                         style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                      />
                                                   </div>
                                                </div>
                                             </Col>
                                          )}
                                          {elm?.attachment && (
                                             <Col md={6}>
                                                <div className="border rounded p-2 h-100 d-flex align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
                                                   <div className="w-100">
                                                      <div className="d-flex align-items-center gap-2 mb-2">
                                                         <i className="ri-attachment-line text-primary" style={{ fontSize: '18px' }}></i>
                                                         <small className="fw-bold">Attachment</small>
                                                      </div>
                                                      <a 
                                                         href={elm?.attachment} 
                                                         target="_blank" 
                                                         rel="noopener noreferrer" 
                                                         className="btn btn-sm btn-primary w-100 d-flex align-items-center justify-content-center gap-2">
                                                         <i className="ri-download-2-line"></i>
                                                         View Attachment
                                                      </a>
                                                   </div>
                                                </div>
                                             </Col>
                                          )}
                                       </Row>

                                       {/* Admin Response Section */}
                                       {(elm?.admin_comments || elm?.admin_signature) && (
                                          <div className="mt-3 p-3 rounded bg-soft-primary" style={{ borderLeft: '4px solid var(--bs-primary)' }}>
                                             <div className="d-flex align-items-center gap-2 mb-3">
                                                <i className="ri-admin-line text-primary" style={{ fontSize: '18px' }}></i>
                                                <strong className="text-primary">Admin Response</strong>
                                             </div>
                                             
                                             <Row className="g-3">
                                                {elm?.admin_signature && (
                                                   <Col md={6}>
                                                      <div className="border rounded p-2 bg-white">
                                                         <small className="fw-bold text-muted d-block mb-2">
                                                            <i className="ri-pen-nib-line text-primary me-1"></i>
                                                            Admin Signature
                                                         </small>
                                                         <div className="border rounded p-2" style={{ height: '100px', backgroundColor: '#f8f9fa' }}>
                                                            <img 
                                                               src={elm?.admin_signature} 
                                                               alt="Admin Signature" 
                                                               style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                            />
                                                         </div>
                                                         {elm?.admin_action_date && (
                                                            <small className="text-muted d-block mt-2">
                                                               <i className="ri-calendar-check-line me-1"></i>
                                                               {Moment(elm?.admin_action_date).format('MMM DD, YYYY [at] hh:mm A')}
                                                            </small>
                                                         )}
                                                      </div>
                                                   </Col>
                                                )}
                                                
                                                {elm?.admin_comments && (
                                                   <Col md={elm?.admin_signature ? 6 : 12}>
                                                      <div className={elm?.admin_signature ? 'h-100' : ''}>
                                                         <small className="fw-bold text-muted d-block mb-2">
                                                            <i className="ri-message-3-line me-1"></i>
                                                            Comments
                                                         </small>
                                                         <p className="mb-0 p-2 bg-white rounded border" style={{ lineHeight: '1.6', minHeight: '60px' }}>
                                                            {elm?.admin_comments}
                                                         </p>
                                                      </div>
                                                   </Col>
                                                )}
                                             </Row>
                                          </div>
                                       )}

                                       {/* Footer Section */}
                                       <div className="mt-3 pt-2 border-top">
                                          <small className="text-muted">
                                             <i className="ri-time-line me-1"></i>
                                             Applied on {Moment(elm?.created_at).format('MMM DD, YYYY [at] hh:mm A')}
                                          </small>
                                       </div>
                                    </div>
                                 )) : (
                                    <div className="text-center py-5">
                                       <NoDataFound />
                                    </div>
                                 )}
                                 {
                                    showMoreDataLoader ?
                                       <div className="text-center py-3">
                                          <img src={loader} alt="loader" style={{ height: "80px" }} />
                                       </div> : null
                                 }
                                 <InView as="div" onChange={(inView, entry) => loadMore(inView)} />
                              </div>
                           </Card.Body>
                        </Card>
                     </Col>
                     {/* Apply Leave Section - Similar to Stories */}
                     <Col lg={4} className="order-1 order-lg-2">
                        <Card>
                           <Card.Header className="d-flex justify-content-between">
                              <div className="header-title">
                                 <h4 className="card-title">Leave Applications</h4>
                              </div>
                           </Card.Header>
                           <Card.Body>
                              <ul className="media-story list-inline m-0 p-0">
                                 <li className="d-flex align-items-center" style={{ cursor: "pointer" }}
                                    onClick={handleShowApplyModal}>
                                    <i className="ri-add-line"></i>
                                    <div className="stories-data ms-3">
                                       <h5>Apply for Leave</h5>
                                       <p className="mb-0">Submit your leave application</p>
                                    </div>
                                 </li>
                              </ul>
                           </Card.Body>
                        </Card>

                        {/* Leave Statistics Card */}
                        <Card className="mt-3">
                           <Card.Header>
                              <div className="header-title">
                                 <h4 className="card-title">Leave Statistics</h4>
                              </div>
                           </Card.Header>
                           <Card.Body>
                              <Row className="g-3">
                                 <Col xs={6}>
                                    <div className="text-center p-3 rounded bg-soft-primary">
                                       <div className="d-flex align-items-center justify-content-center mb-2">
                                          <i className="ri-file-list-3-line text-primary" style={{ fontSize: '32px' }}></i>
                                       </div>
                                       <h3 className="mb-0 text-primary fw-bold">{leaveApplicationList?.length || 0}</h3>
                                       <p className="mb-0 text-muted small">Total Leaves</p>
                                    </div>
                                 </Col>
                                 <Col xs={6}>
                                    <div className="text-center p-3 rounded bg-soft-primary">
                                       <div className="d-flex align-items-center justify-content-center mb-2">
                                          <i className="ri-checkbox-circle-line text-primary" style={{ fontSize: '32px' }}></i>
                                       </div>
                                       <h3 className="mb-0 text-primary fw-bold">{leaveApplicationList?.filter(leave => leave.status === 1).length || 0}</h3>
                                       <p className="mb-0 text-muted small">Approved</p>
                                    </div>
                                 </Col>
                                 <Col xs={6}>
                                    <div className="text-center p-3 rounded bg-soft-warning">
                                       <div className="d-flex align-items-center justify-content-center mb-2">
                                          <i className="ri-time-line text-warning" style={{ fontSize: '32px' }}></i>
                                       </div>
                                       <h3 className="mb-0 text-warning fw-bold">{leaveApplicationList?.filter(leave => leave.status === 0).length || 0}</h3>
                                       <p className="mb-0 text-muted small">Pending</p>
                                    </div>
                                 </Col>
                                 <Col xs={6}>
                                    <div className="text-center p-3 rounded bg-soft-danger">
                                       <div className="d-flex align-items-center justify-content-center mb-2">
                                          <i className="ri-close-circle-line text-danger" style={{ fontSize: '32px' }}></i>
                                       </div>
                                       <h3 className="mb-0 text-danger fw-bold">{leaveApplicationList?.filter(leave => leave.status === 2).length || 0}</h3>
                                       <p className="mb-0 text-muted small">Rejected</p>
                                    </div>
                                 </Col>
                              </Row>
                           </Card.Body>
                        </Card>

                        {/* Calendar View Card */}
                        <Card className="mt-3">
                           <Card.Header className="d-flex justify-content-between align-items-center py-2">
                              <h5 className="card-title mb-0">
                                 <i className="ri-calendar-line me-2 text-primary"></i>
                                 {currentCalendarDate.format('MMM YYYY')}
                              </h5>
                              <div className="btn-group btn-group-sm">
                                 <Button variant="outline-secondary" size="sm" onClick={goToPreviousMonth}>
                                    <i className="ri-arrow-left-s-line"></i>
                                 </Button>
                                 <Button variant="outline-secondary" size="sm" onClick={goToToday}>
                                    <i className="ri-refresh-line"></i>
                                 </Button>
                                 <Button variant="outline-secondary" size="sm" onClick={goToNextMonth}>
                                    <i className="ri-arrow-right-s-line"></i>
                                 </Button>
                              </div>
                           </Card.Header>
                           <Card.Body className="p-2">
                              {/* Weekday Headers */}
                              <div className="d-flex mb-1">
                                 {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                                    <div key={idx} className="flex-fill text-center py-1">
                                       <small className="text-muted fw-bold" style={{ fontSize: '11px' }}>{day}</small>
                                    </div>
                                 ))}
                              </div>

                              {/* Calendar Grid */}
                              <div>
                                 {Array.from({ length: Math.ceil(generateCalendarDays().length / 7) }).map((_, weekIndex) => (
                                    <div key={weekIndex} className="d-flex">
                                       {generateCalendarDays().slice(weekIndex * 7, (weekIndex + 1) * 7).map((day, dayIndex) => {
                                          const isCurrentMonth = day.month() === currentCalendarDate.month();
                                          const isToday = day.isSame(Moment(), 'day');
                                          const leavesForDay = getLeavesForDate(day);
                                          const hasLeave = leavesForDay.length > 0;
                                          const approvedCount = leavesForDay.filter(l => l.status === 1).length;
                                          const pendingCount = leavesForDay.filter(l => l.status === 0).length;
                                          const rejectedCount = leavesForDay.filter(l => l.status === 2).length;

                                          return (
                                             <div 
                                                key={dayIndex} 
                                                className="flex-fill text-center position-relative"
                                                style={{ 
                                                   padding: '6px 2px',
                                                   minHeight: '38px',
                                                   cursor: hasLeave ? 'pointer' : 'default'
                                                }}
                                                title={hasLeave ? leavesForDay.map(l => `${l.leave_type} - ${l.student?.first_name}`).join('\n') : ''}
                                             >
                                                <div 
                                                   className={`rounded-circle d-inline-flex align-items-center justify-content-center ${isToday ? 'bg-primary text-white fw-bold' : hasLeave ? 'bg-soft-primary text-primary fw-semibold' : isCurrentMonth ? 'text-dark' : 'text-muted'}`}
                                                   style={{ 
                                                      width: '28px', 
                                                      height: '28px', 
                                                      fontSize: '12px',
                                                      transition: 'all 0.2s'
                                                   }}
                                                >
                                                   {day.format('D')}
                                                </div>
                                                {hasLeave && (
                                                   <div className="position-absolute bottom-0 start-50 translate-middle-x d-flex gap-1" style={{ marginBottom: '2px' }}>
                                                      {approvedCount > 0 && (
                                                         <div style={{ 
                                                            width: '4px', 
                                                            height: '4px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: 'var(--bs-primary)'
                                                         }}></div>
                                                      )}
                                                      {pendingCount > 0 && (
                                                         <div style={{ 
                                                            width: '4px', 
                                                            height: '4px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: 'var(--bs-warning)'
                                                         }}></div>
                                                      )}
                                                      {rejectedCount > 0 && (
                                                         <div style={{ 
                                                            width: '4px', 
                                                            height: '4px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: 'var(--bs-danger)'
                                                         }}></div>
                                                      )}
                                                   </div>
                                                )}
                                             </div>
                                          );
                                       })}
                                    </div>
                                 ))}
                              </div>

                              {/* Legend */}
                              <div className="border-top pt-2 mt-2">
                                 <div className="d-flex justify-content-center gap-3 flex-wrap">
                                    <small className="d-flex align-items-center gap-1">
                                       <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--bs-primary)', display: 'inline-block' }}></span>
                                       <span className="text-muted">Approved</span>
                                    </small>
                                    <small className="d-flex align-items-center gap-1">
                                       <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--bs-warning)', display: 'inline-block' }}></span>
                                       <span className="text-muted">Pending</span>
                                    </small>
                                    <small className="d-flex align-items-center gap-1">
                                       <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--bs-danger)', display: 'inline-block' }}></span>
                                       <span className="text-muted">Rejected</span>
                                    </small>
                                 </div>
                              </div>
                           </Card.Body>
                        </Card>
                     </Col>
               </Row>
            </Container>
         </div>

         {/* Apply Leave Modal */}
         <Modal show={showApplyModal} onHide={handleCloseApplyModal} centered size="lg" style={{marginTop: '50px', paddingBottom: '50px'}}>
            <Modal.Header closeButton style={{
               padding: '12px 16px',
               paddingRight: '40px',
               borderRadius: '6px 6px 0 0',
               borderBottom: '1px solid #dee2e6',
               marginTop: '0px'
            }}>
               <Modal.Title style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  paddingRight: '10px'
               }}>
                  Apply for Leave
               </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ marginTop: '0', paddingTop: '20px' }}>
               <Form onSubmit={handleApplyLeave}>
                  <Form.Group className="mb-3">
                     <Form.Label>Leave Type <span className="text-danger">*</span></Form.Label>
                     <Form.Select
                        value={leaveType}
                        onChange={(e) => setLeaveType(e.target.value)}
                        required
                     >
                        <option value="">Select Leave Type</option>
                        <option value="sick">Sick Leave</option>
                        <option value="vacation">Vacation</option>
                        <option value="emergency">Emergency Leave</option>
                        <option value="family">Family Leave</option>
                        <option value="other">Other</option>
                     </Form.Select>
                  </Form.Group>

                  <Row>
                     <Col md={6}>
                        <Form.Group className="mb-3">
                           <Form.Label>From Date <span className="text-danger">*</span></Form.Label>
                           <Form.Control
                              type="date"
                              value={fromDate}
                              onChange={(e) => {
                                 setFromDate(e.target.value);
                                 // Reset toDate if it's before the new fromDate
                                 if (toDate && e.target.value > toDate) {
                                    setToDate('');
                                 }
                              }}
                              required
                           />
                        </Form.Group>
                     </Col>
                     <Col md={6}>
                        <Form.Group className="mb-3">
                           <Form.Label>To Date <span className="text-danger">*</span></Form.Label>
                           <Form.Control
                              type="date"
                              value={toDate}
                              onChange={(e) => setToDate(e.target.value)}
                              min={fromDate || undefined}
                              required
                           />
                        </Form.Group>
                     </Col>
                  </Row>

                  <Form.Group className="mb-3">
                     <Form.Label>Reason <span className="text-danger">*</span></Form.Label>
                     <Form.Control
                        as="textarea"
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Please provide a reason for your leave application..."
                        required
                     />
                  </Form.Group>

                  <Form.Group className="mb-3">
                     <Form.Label>Parent Signature <span className="text-danger">*</span></Form.Label>
                     <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px' }}>
                        <SignatureCanvas
                           ref={signaturePadRef}
                           canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '100%' }
                           }}
                        />
                     </div>
                     <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={handleClearSignature}
                        type="button"
                     >
                        Clear Signature
                     </Button>
                     <Form.Text className="text-muted d-block mt-2">
                        Please sign above using your mouse or touch screen
                     </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                     <Form.Label>Attachment (Optional)</Form.Label>
                     <Form.Control
                        type="file"
                        onChange={(e) => setAttachment(e.target.files[0])}
                        accept="image/*,.pdf,.doc,.docx"
                     />
                     <Form.Text className="text-muted">
                        Upload supporting documents (Images, PDF, Word documents)
                     </Form.Text>
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                     <Button variant="secondary" onClick={handleCloseApplyModal} disabled={isSubmitting}>
                        Cancel
                     </Button>
                     <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                     </Button>
                  </div>
               </Form>
            </Modal.Body>
         </Modal>
      </>
   )
}


//     return (
//         <>
//             <ProfileHeader title="Follower List" img={img3} />
//             <div id="content-page" className="content-page">
//                 <Container>
//                     <Row>
//                         {followerList?.length > 0 ? followerList.map((elm, i) => (
//                             <Col md={6}>
//                                 <Card className=" card-block card-stretch card-height">
//                                     <Card.Body className=" profile-page p-0">
//                                         <div className="profile-header-image">
//                                             <div className="cover-container">
//                                                 <img loading="lazy" src={img1} alt="profile-bg" className="rounded img-fluid w-100" />
//                                             </div>
//                                             <div className="profile-info p-4">
//                                                 <div className="user-detail">
//                                                     <div className="d-flex flex-wrap justify-content-between align-items-start">
//                                                         <div className="profile-detail d-flex">
//                                                             <div className="profile-img pe-4">
//                                                                 <img style={{height: 120, width: 120}} loading="lazy" src={elm?.follower?.user_setting?.photo ?
//                                                                     elm?.follower?.user_setting?.photo : "https://uzone360live.s3.amazonaws.com/noimage.png"}
//                                                                     alt="profile-img" className="avatar-130 img-fluid" />
//                                                             </div>
//                                                             <div className="user-data-block">
//                                                                 <h4>
//                                                                     <Link to={`/dashboard/app/friend-profile/${elm?.follower?.slug}`}>{elm?.follower?.first_name && (elm?.follower?.first_name + " " + elm?.follower?.last_name)}</Link>
//                                                                 </h4>
//                                                                 <h6>{elm?.follower?.slug && (elm?.follower?.slug)}</h6>
//                                                                 {/* <p>Lorem Ipsum is simply dummy text of the</p> */}
//                                                             </div>
//                                                         </div>
//                                                         {
//                                                             elm?.isblocked == 0 ?
//                                                                 <Link to="#" className="me-3 btn btn-primary rounded"
//                                                                     onClick={() => {
//                                                                         getFollowUnfollow(elm?.follower_id, i, 1)
//                                                                     }}>Block</Link> :
//                                                                 <Link to="#" className="me-3 btn btn-secondary rounded"
//                                                                     onClick={() => {
//                                                                         getFollowUnfollow(elm?.follower_id, i, 0)
//                                                                     }}>Unblocked</Link>
//                                                         }
//                                                         {/* <button type="submit" className="btn btn-primary"> Block User</button> */}
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         </div>
//                                     </Card.Body>
//                                 </Card>
//                             </Col>
//                         )) : null
//                         }
//                         {
//                             page < maxPage ?
//                                 <li className="d-block text-center mb-0 pb-0">
//                                     <Link to="#" className="me-3 btn btn-primary"
//                                         onClick={() => {
//                                             setPage(page + 1)
//                                             getFollowerList()
//                                         }}>View More Request</Link>
//                                 </li> : null
//                         }
//                     </Row>
//                 </Container>
//             </div>
//         </>
//     )

// }

export default LeaveApplication;